import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  collectorAreas,
  createArea,
  listAreas,
  setCollectorAreas,
} from "../../api/areas";
import type { OperatorLogin } from "../../api/operatorLogins";
import { useIdentity } from "../../auth/useIdentity";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * The areas a door collector works (owner 2026-11-02). Tick the areas; a new
 * area is added here too. Customers get their area from the import (area and
 * card_no columns). An unticked area is turned off for this collector, never
 * deleted.
 */
export function CollectorAreasDialog({
  ns,
  login,
  onClose,
}: {
  ns: string;
  login: OperatorLogin | null;
  onClose: () => void;
}) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string[] | null>(null);
  const [newName, setNewName] = useState("");
  const all = useQuery({
    queryKey: ["areas", ns],
    enabled: Boolean(identity.token && ns && login),
    queryFn: ({ signal }) => listAreas(identity, ns, signal),
  });
  const mine = useQuery({
    queryKey: ["collectorareas", ns, login?.id],
    enabled: Boolean(identity.token && ns && login),
    queryFn: ({ signal }) => collectorAreas(identity, ns, login!.id, signal),
  });
  const value = picked ?? mine.data ?? [];
  const add = useMutation({
    mutationFn: () => createArea(identity, ns, newName.trim()),
    onSuccess: async (a) => {
      setNewName("");
      setPicked([...value.filter((x) => x !== a.id), a.id]);
      await qc.invalidateQueries({ queryKey: ["areas", ns] });
    },
  });
  const save = useMutation({
    mutationFn: () => setCollectorAreas(identity, ns, login!.id, value),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["collectorareas", ns, login?.id],
      });
      setPicked(null);
      onClose();
    },
  });
  const close = () => {
    setPicked(null);
    add.reset();
    save.reset();
    onClose();
  };

  return (
    <Dialog open={login !== null} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>
        Areas for {login ? `${login.firstname} ${login.lastname}` : ""}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          The collector sees only the customers of these areas. A customer gets
          an area from the import (area and card_no columns).
        </Typography>
        {(all.error || mine.error) && (
          <Alert severity="error">{errText(all.error ?? mine.error)}</Alert>
        )}
        {all.data && all.data.length === 0 && (
          <Alert severity="info">No areas yet. Add one below.</Alert>
        )}
        <FormGroup>
          {all.data
            ?.filter((a) => a.enabled)
            .map((a) => (
              <FormControlLabel
                key={a.id}
                control={
                  <Checkbox
                    checked={value.includes(a.id)}
                    onChange={(e) =>
                      setPicked(
                        e.target.checked
                          ? [...value, a.id]
                          : value.filter((x) => x !== a.id),
                      )
                    }
                  />
                }
                label={`${a.name} (${a.customers} customer${a.customers === 1 ? "" : "s"})`}
              />
            ))}
        </FormGroup>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="New area"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            disabled={!newName.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            Add
          </Button>
        </Stack>
        {add.error && <Alert severity="error">{errText(add.error)}</Alert>}
        {save.error && <Alert severity="error">{errText(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={save.isPending || !mine.data}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
